import type { ReactElement } from 'react'
import { useMemo } from 'react'

import { USER_ROLE, ADMIN_ROLES, DEPARTMENT_ROLES } from '@/hooks/access'
import useUser from '@/hooks/user'
import { AccountSettings } from '@/modules/account-settings'
import { AddressListPage } from '@/modules/admin/addresses'
import { AppMessagePage } from '@/modules/admin/app-message'
import { AssetLocationsListPage } from '@/modules/admin/asset-locations'
import { AssetLocationDetails } from '@/modules/admin/asset-locations/AssetLocationDetails'
import { NewAssetLocation } from '@/modules/admin/asset-locations/NewAssetLocation'
import Assets from '@/modules/admin/assets'
import { AssetDetails } from '@/modules/admin/assets/AssetDetails'
import { NewAsset } from '@/modules/admin/assets/NewAsset'
import { TradeIn } from '@/modules/admin/assets/TradeIn/orders'
import CostObjects from '@/modules/admin/cost-objects'
import { CostObjectDetails } from '@/modules/admin/cost-objects/CostObjectDetails'
import { DepartmentsListPage } from '@/modules/admin/departments'
import { DrillDownContainer } from '@/modules/admin/drilldown'
import { ChoseReportType } from '@/modules/admin/drilldown/ChoseReportType'
import { CostObjectReport } from '@/modules/admin/drilldown/cost-object/CostObjectReport'
import { ProductReportByCostObject } from '@/modules/admin/drilldown/cost-object/ProductReportByCostObject'
import { CostObjectReportByDepartment } from '@/modules/admin/drilldown/department/CostObjectReportByDepartment'
import { DepartmentReport } from '@/modules/admin/drilldown/department/DepratmentReport'
import { ProductReportByDepartmentAndCostObject } from '@/modules/admin/drilldown/department/ProductReportByDepartmentAndCostObject'
import { OrgChart } from '@/modules/admin/drilldown/org-chart/OrgChart'
import { CategoryReportByService } from '@/modules/admin/drilldown/service/CategoryReportByService'
import { CostObjectsReportByServiceAndCategoryAndGroup } from '@/modules/admin/drilldown/service/CostObjectsReportByServiceAndCategoryAndGroups'
import { GroupReportByServiceAndCategory } from '@/modules/admin/drilldown/service/GroupReportByServiceAndCategory'
import { ServiceReport } from '@/modules/admin/drilldown/service/ServiceReport'
import { EmployeeConsents } from '@/modules/admin/employee-consents'
import { EmployeeConsentReport } from '@/modules/admin/employee-consents/report'
import { Features } from '@/modules/admin/features'
import { InvoicesListPage } from '@/modules/admin/inovices'
import { Reports } from '@/modules/admin/reports'
import { SettingsPage } from '@/modules/admin/settings'
import { SMSListPage } from '@/modules/admin/sms-sender'
import { TelePoliciesListPage } from '@/modules/admin/tele-policies'
import { TextTemplates } from '@/modules/admin/text-templates'
import { Users } from '@/modules/admin/users'
import { WorkflowsPage } from '@/modules/admin/workflows'
import DeviceApplicationListPage from '@/modules/applications/device'
import { DeviceApplicationPage } from '@/modules/applications/device/DeviceApplicationPage'
import InjuryReportList from '@/modules/applications/injury-reports'
import InjuryReport from '@/modules/applications/injury-reports/InjuryReportPage'
import SdaList from '@/modules/applications/salary-deduction'
import { Sda } from '@/modules/applications/salary-deduction/SalaryDeductionPage'
import { Dashboard } from '@/modules/dashboard/Dashboard'
import EcomPolicyList from '@/modules/ecom-policy/list'
import EcomNewPolicy from '@/modules/ecom-policy/new'
import EcomPolicyOrder from '@/modules/ecom-policy/order'
import EcomPolicyOrderList from '@/modules/ecom-policy/orders'
import EcomPolicy from '@/modules/ecom-policy/policy/Policy'
import ShopNewOrder from '@/modules/ecom-policy/shop/new-order/ShopNewOrder'
import { MenuAdmin } from '@/modules/menu/MenuAdmin'
import { MenuEmployees } from '@/modules/menu/MenuEmployees'
import { MenuHardware } from '@/modules/menu/MenuHardware'
import { MenuReports } from '@/modules/menu/MenuReports'
import { MenuSubscriptions } from '@/modules/menu/MenuSubscriptions'
import Showcase from '@/modules/showcase/Showcase'
import NewSubscriptionList from '@/modules/subscriptions/subscription'
import NewSubscriptionForm from '@/modules/subscriptions/subscription/new'
import { SubscriptionPage } from '@/modules/subscriptions/subscription/SubscriptionPage'
import TerminationList from '@/modules/subscriptions/termination'
import { NewTerminationForm } from '@/modules/subscriptions/termination/new'
import { TerminationPage } from '@/modules/subscriptions/termination/TerminationPage'
import AllSystemMessagesPage from '@/modules/system-messages'
import type { UserRole } from '@/types'
import { APP_ROUTES } from '@/util/app-routes'

import { NotFound } from './NotFound'

export type Route = {
  path: string
  element: ReactElement
  auth?: boolean
  allowedRoles: UserRole[]
}

export const routes: Route[] = [
  {
    path: '*',
    element: <Dashboard />,
    allowedRoles: [],
  },
  {
    path: APP_ROUTES.SHOWCASE,
    element: <Showcase />,
    allowedRoles: [],
  },
  {
    path: APP_ROUTES.SYSTEM_MESSAGES,
    element: <AllSystemMessagesPage />,
    allowedRoles: [],
  },
  {
    path: APP_ROUTES.ACCOUNT_SETTINGS,
    element: <AccountSettings />,
    allowedRoles: [],
  },
  // DEVICE ORDERS
  {
    path: APP_ROUTES.DEVICE_ORDERS,
    element: <DeviceApplicationListPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.DEVICE_ORDER,
    element: <DeviceApplicationPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },

  // EMPLOYEES
  {
    path: APP_ROUTES.EMPLOYEE,
    element: <CostObjectDetails />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.EMPLOYEES,
    element: <CostObjects />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.EMPLOYEES_SALARY_DEDUCTION_APPLICATIONS,
    element: <SdaList />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.EMPLOYEES_SALARY_DEDUCTION_APPLICATION,
    element: <Sda />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },

  // HARDWARE
  {
    path: APP_ROUTES.HARDWARE_INJURY_REPORTS,
    element: <InjuryReportList />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.HARDWARE_INJURY_REPORT,
    element: <InjuryReport />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.HARDWARE_SHOP,
    element: <ShopNewOrder />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER_LIST,
    element: <EcomPolicyOrderList />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER,
    element: <EcomPolicyOrder />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.HARDWARE_ECOM_POLICY_LIST,
    element: <EcomPolicyList />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.HARDWARE_ECOM_POLICY,
    element: <EcomPolicy />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.HARDWARE_ECOM_POLICY_NEW,
    element: <EcomNewPolicy />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.HARDWARE_ASSETS,
    element: <Assets />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.HARDWARE_ASSET,
    element: <AssetDetails />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.HARDWARE_ASSETS_NEW,
    element: <NewAsset />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.HARDWARE_ASSET_LOCATION_LIST,
    element: <AssetLocationsListPage />,
    allowedRoles: [...ADMIN_ROLES, USER_ROLE.IT_USER, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.HARDWARE_ASSET_LOCATION,
    element: <AssetLocationDetails />,
    allowedRoles: [...ADMIN_ROLES, USER_ROLE.IT_USER, USER_ROLE.IT_PURCHASER],
  },
  {
    path: APP_ROUTES.HARDWARE_ASSET_LOCATION_NEW,
    element: <NewAssetLocation />,
    allowedRoles: [...ADMIN_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.HARDWARE_TRADE_IN,
    element: <TradeIn />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },

  // SUBSCRIPTIONS
  {
    path: APP_ROUTES.SUBSCRIPTIONS_NEW_SUBSCRIPTION_LIST_ADD,
    element: <NewSubscriptionForm />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.SUBSCRIPTIONS_NEW_SUBSCRIPTION_LIST,
    element: <NewSubscriptionList />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.SUBSCRIPTIONS_SUBSCRIPTION,
    element: <SubscriptionPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.SUBSCRIPTIONS_TERMINATION_LIST_ADD,
    element: <NewTerminationForm />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.SUBSCRIPTIONS_TERMINATION_LIST,
    element: <TerminationList />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.SUBSCRIPTIONS_TERMINATION,
    element: <TerminationPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.SUBSCRIPTIONS_TELE_POLICIES,
    element: <TelePoliciesListPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },

  // REPORTS
  {
    path: APP_ROUTES.REPORTS,
    element: <Reports />,
    allowedRoles: [...ADMIN_ROLES, USER_ROLE.DEPARTMENT_HEAD_CUSTOMER_HEAD, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_INVOICES,
    element: <InvoicesListPage />,
    allowedRoles: [...ADMIN_ROLES, USER_ROLE.DEPARTMENT_HEAD_CUSTOMER_HEAD, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN,
    element: (
      <DrillDownContainer>
        <OrgChart />
      </DrillDownContainer>
    ),
    allowedRoles: [USER_ROLE.ADMIN, USER_ROLE.CUSTOMER_HEAD_ADMIN, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN,
    element: (
      <DrillDownContainer>
        <ChoseReportType />
      </DrillDownContainer>
    ),
    allowedRoles: [USER_ROLE.CUSTOMER_ADMIN, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN_SERVICE,
    element: (
      <DrillDownContainer>
        <ServiceReport />
      </DrillDownContainer>
    ),
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN_SERVICE_CATEGORY,
    element: (
      <DrillDownContainer>
        <CategoryReportByService />
      </DrillDownContainer>
    ),
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN_SERVICE_CATEGORY_GROUPS,
    element: (
      <DrillDownContainer>
        <GroupReportByServiceAndCategory />
      </DrillDownContainer>
    ),
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN_SERVICE_CATEGORY_GROUPS_COST_OBJECTS,
    element: (
      <DrillDownContainer>
        <CostObjectsReportByServiceAndCategoryAndGroup />
      </DrillDownContainer>
    ),
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN_DEPARTMENT,
    element: (
      <DrillDownContainer>
        <DepartmentReport />
      </DrillDownContainer>
    ),
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN_DEPARTMENT_COST_OBJECT,
    element: (
      <DrillDownContainer>
        <CostObjectReportByDepartment />
      </DrillDownContainer>
    ),
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN_DEPARTMENT_COST_OBJECT_PRODUCTS,
    element: (
      <DrillDownContainer>
        <ProductReportByDepartmentAndCostObject />
      </DrillDownContainer>
    ),
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN_COST_OBJECT,
    element: (
      <DrillDownContainer>
        <CostObjectReport />
      </DrillDownContainer>
    ),
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
  },
  {
    path: APP_ROUTES.REPORTS_DRILL_DOWN_COST_OBJECT_PRODUCT,
    element: (
      <DrillDownContainer>
        <ProductReportByCostObject />
      </DrillDownContainer>
    ),
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.REPORT_USER],
  },

  // ADMIN
  {
    path: APP_ROUTES.ADMIN,
    element: <CostObjects />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.ADMIN_ADDRESSES,
    element: <AddressListPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.ADMIN_TEXT_TEMPLATES,
    element: <TextTemplates />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.ADMIN_USERS,
    element: <Users />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.ADMIN_EMPLOYEE_CONSENTS,
    element: <EmployeeConsents />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.ADMIN_EMPLOYEE_CONSENT_REPORT,
    element: <EmployeeConsentReport />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.ADMIN_DEPARTMENTS,
    element: <DepartmentsListPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.ADMIN_SMS_SENDER,
    element: <SMSListPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.ADMIN_INVOICES,
    element: <InvoicesListPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.ADMIN_TRADE_IN,
    element: <TradeIn />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.ADMIN_FEATURES,
    element: <Features />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.ADMIN_APP_MESSAGE,
    element: <AppMessagePage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.ADMIN_SETTINGS,
    element: <SettingsPage />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES, USER_ROLE.IT_USER],
  },
  {
    path: APP_ROUTES.ADMIN_WORKFLOWS,
    element: <WorkflowsPage />,
    allowedRoles: [...ADMIN_ROLES],
  },

  // MENU
  {
    path: APP_ROUTES.MENU_ADMIN,
    element: <MenuAdmin />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.MENU_HARDWARE,
    element: <MenuHardware />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.MENU_SUBSCRIPTIONS,
    element: <MenuSubscriptions />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.MENU_EMPLOYEES,
    element: <MenuEmployees />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
  {
    path: APP_ROUTES.MENU_REPORTS,
    element: <MenuReports />,
    allowedRoles: [...ADMIN_ROLES, ...DEPARTMENT_ROLES],
  },
]

export const useRoutes = () => {
  const user = useUser()

  return useMemo(() => {
    const list: Route[] = []
    let lastRoute = ''

    routes.forEach(route => {
      // If user is logged in and route is protected, check if user has access
      // if allowedRoles has no items inside, it means accessible for all users
      if (user && (route.allowedRoles.length === 0 || route.allowedRoles.includes(user.role))) {
        // If same route has different components based on user role, replace it
        if (lastRoute === route.path) {
          list[list.length - 1] = route
        } else {
          list.push(route)
        }
      } else {
        list.push({
          ...route,
          element: <NotFound />,
        })
      }
      lastRoute = route.path
    })

    return list
  }, [user])
}
